import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";
import { FaShip, FaCalendarAlt, FaUsers, FaUser, FaTag, FaStickyNote, FaHandshake } from "react-icons/fa"; // Updated icons
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner"; // Assuming path is correct
import { yachts, yachtBookings, coupons, partners } from "../../services/api"; // Use yacht and yachtBooking services
import moment from "moment-timezone";
import Swal from 'sweetalert2';

// Define cruise types based on backend validation
const CRUISE_TYPES = ['Private Half Day', 'Private Full Day', 'Semi-Private Half Day'];

const CreateYachtBookingModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [availableYachts, setAvailableYachts] = useState([]);
  const [allPartners, setAllPartners] = useState([]); // State for partners
  const [selectedYacht, setSelectedYacht] = useState(null); // Store the whole yacht object
  const [errors, setErrors] = useState({});
  const [couponStatus, setCouponStatus] = useState({ isValid: false, discount: 0, message: "" }); // Keep coupon logic if needed backend handles validation
  const [searchQuery, setSearchQuery] = useState(""); // For filtering yachts
  const [selectedYachtType, setSelectedYachtType] = useState(""); // For filtering yachts

  const [formData, setFormData] = useState({
    yachtId: "",
    bookingDate: null,
    cruiseType: CRUISE_TYPES[0], // Default cruise type
    numberOfGuests: 1,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    couponCode: "",
    additionalNotes: "",
    professionalPartnerId: "", // For selecting a partner
  });

  // Fetch initial data (yachts and partners) when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    } else {
      resetForm(); // Reset form when closing
    }
  }, [isOpen]);

  const fetchInitialData = async () => {
    try {
      setInitialLoading(true);
      const [yachtsRes, partnersRes] = await Promise.all([
        yachts.getAll({ status: 'available' }), // Fetch only available yachts
        partners.getAll()
      ]);
      setAvailableYachts(yachtsRes.data || []);
      setAllPartners(partnersRes.data || []);
      setInitialLoading(false);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      Swal.fire("Error", "Could not load yachts or partners.", "error");
      setInitialLoading(false);
    }
  };

  // Reset form state
  const resetForm = () => {
    setFormData({
      yachtId: "",
      bookingDate: null,
      cruiseType: CRUISE_TYPES[0],
      numberOfGuests: 1,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      couponCode: "",
      additionalNotes: "",
      professionalPartnerId: "",
    });
    setSelectedYacht(null);
    setErrors({});
    setCouponStatus({ isValid: false, discount: 0, message: "" });
    setSearchQuery("");
    setSelectedYachtType("");
  };

  // Handle changes in form inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If yacht selection changes, update selectedYacht object
    if (name === "yachtId") {
      const yacht = availableYachts.find(y => y._id === value);
      setSelectedYacht(yacht || null);
       // Clear errors related to yacht selection or capacity
      setErrors(prev => ({ ...prev, yachtId: undefined, numberOfGuests: undefined }));
    }

     // Validate guest capacity if number of guests or yacht changes
     if (name === 'numberOfGuests' || name === 'yachtId') {
         const guests = parseInt(name === 'numberOfGuests' ? value : formData.numberOfGuests);
         const currentSelectedYacht = name === 'yachtId' ? availableYachts.find(y => y._id === value) : selectedYacht;
         if (currentSelectedYacht && guests > currentSelectedYacht.guestCapacity) {
             setErrors(prev => ({ ...prev, numberOfGuests: `Maximum ${currentSelectedYacht.guestCapacity} guests allowed for this yacht.` }));
         } else {
             setErrors(prev => ({ ...prev, numberOfGuests: undefined }));
         }
     }
  };

  // Handle date changes
  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, bookingDate: date }));
     setErrors(prev => ({ ...prev, bookingDate: undefined })); // Clear date error on change
  };

  // Validate the form before submission
  const validateForm = () => {
    const newErrors = {};
    if (!formData.yachtId) newErrors.yachtId = "Please select a yacht";
    if (!formData.bookingDate) newErrors.bookingDate = "Booking date is required";
    if (!formData.cruiseType) newErrors.cruiseType = "Cruise type is required";
    if (!formData.numberOfGuests || formData.numberOfGuests < 1) newErrors.numberOfGuests = "Number of guests must be at least 1";
    if (selectedYacht && formData.numberOfGuests > selectedYacht.guestCapacity) {
         newErrors.numberOfGuests = `Maximum ${selectedYacht.guestCapacity} guests allowed for ${selectedYacht.name}.`;
    }
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire("Validation Error", "Please fix the errors in the form.", "warning");
      return;
    }

    setLoading(true);
    try {
      // Prepare data for API - backend calculates price
      const bookingData = {
        yachtId: formData.yachtId,
        customer: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phoneNumber: formData.phone.trim(),
          // Note: Age is not in the YachtBooking schema provided
        },
        bookingDate: formData.bookingDate.toISOString(),
        cruiseType: formData.cruiseType,
        numberOfGuests: parseInt(formData.numberOfGuests),
        couponCode: formData.couponCode.trim() || undefined, // Send only if not empty
        additionalNotes: formData.additionalNotes.trim() || undefined,
        professionalPartnerId: formData.professionalPartnerId || undefined, // Send only if selected
      };

      const response = await yachtBookings.create(bookingData);
      Swal.fire({
        icon: 'success',
        title: 'Booking Created!',
        text: `Booking for ${selectedYacht?.name} on ${moment(formData.bookingDate).format("DD/MM/YYYY")} created successfully.`,
        timer: 2500,
        showConfirmButton: false
      });
      onSuccess(response.data); // Pass created booking data back if needed
      onClose(); // Close modal on success
    } catch (error) {
      console.error("Error creating yacht booking:", error);
      Swal.fire({
        icon: 'error',
        title: 'Booking Failed',
        text: `Could not create booking: ${error.message}`,
        confirmButtonColor: '#d33',
      });
      setErrors({ submit: `Failed to create booking: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  // Filter yachts based on search and type
  const filteredYachts = availableYachts.filter((yacht) => {
    const matchesSearch =
      !searchQuery ||
      `${yacht.name} ${yacht.builder}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesType =
      !selectedYachtType || yacht.yachtType === selectedYachtType;
    return matchesSearch && matchesType;
  });

  // Custom input for DatePicker
  const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, isError }, ref) => (
    <button
      type="button"
      className={`w-full flex items-center px-4 py-2 border ${
        isError ? "border-red-500 bg-red-50" : "border-gray-300"
      } rounded-md transition-all hover:border-blue-500 text-left text-sm ${value ? 'text-gray-900' : 'text-gray-400'}`}
      onClick={onClick}
      ref={ref}
    >
      <FaCalendarAlt className="text-gray-500 mr-2" />
      {value || placeholder}
    </button>
  ));


  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-4xl relative max-h-[95vh] flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-700 to-blue-800 px-6 py-4 flex justify-between items-center sticky top-0 z-20 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <FaShip className="text-white text-2xl" />
              <h2 className="text-xl font-bold text-white">Create Yacht Booking</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors text-white"
              aria-label="Close modal"
            >
              <IoClose size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-grow">
            {initialLoading ? (
              <div className="p-8 flex flex-col items-center justify-center">
                <LoadingSpinner size="lg" />
                <p className="mt-4 text-gray-500">Loading yachts & partners...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Yacht Selection */}
                 <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center"><FaShip className="mr-2 text-blue-600"/>Select Yacht *</h4>
                     {errors.yachtId && <p className="text-red-500 text-xs mb-2">{errors.yachtId}</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                         <input
                            type="text"
                            placeholder="Search by name or builder..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                         />
                          <select
                            value={selectedYachtType}
                            onChange={(e) => setSelectedYachtType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">All Types</option>
                             {Array.from(new Set(availableYachts.map(y => y.yachtType))).map(type => (
                                 <option key={type} value={type}>{type}</option>
                             ))}
                          </select>
                    </div>
                    <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
                        {filteredYachts.length > 0 ? filteredYachts.map(yacht => (
                             <label key={yacht._id} className={`flex items-center p-3 rounded-md cursor-pointer hover:bg-blue-50 transition-colors ${formData.yachtId === yacht._id ? 'bg-blue-100 border border-blue-300' : 'border border-transparent'}`}>
                                 <input
                                    type="radio"
                                    name="yachtId"
                                    value={yacht._id}
                                    checked={formData.yachtId === yacht._id}
                                    onChange={handleChange}
                                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                 <span className="text-sm font-medium text-gray-800">{yacht.name} ({yacht.builder}) - {yacht.guestCapacity} Guests</span>
                             </label>
                        )) : <p className="text-sm text-gray-500 text-center p-4">No available yachts match your search.</p>}
                    </div>
                 </div>

                {/* Booking Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Booking Date *</label>
                    <DatePicker
                      selected={formData.bookingDate}
                      onChange={handleDateChange}
                      minDate={moment().add(1, 'days').toDate()} // Allow booking from tomorrow onwards
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select booking date"
                      customInput={<CustomDateInput isError={!!errors.bookingDate} />}
                      wrapperClassName="w-full"
                      disabled={!selectedYacht} // Disable if no yacht selected
                    />
                    {errors.bookingDate && <p className="text-red-500 text-xs mt-1">{errors.bookingDate}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cruise Type *</label>
                    <select
                      name="cruiseType"
                      value={formData.cruiseType}
                      onChange={handleChange}
                      required
                      disabled={!selectedYacht}
                      className={`w-full px-3 py-2 border ${errors.cruiseType ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${!selectedYacht ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      {CRUISE_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests *</label>
                    <input
                      type="number"
                      name="numberOfGuests"
                      value={formData.numberOfGuests}
                      onChange={handleChange}
                      required
                      min="1"
                      max={selectedYacht?.guestCapacity || 100} // Set max based on selected yacht
                      disabled={!selectedYacht}
                      className={`w-full px-3 py-2 border ${errors.numberOfGuests ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${!selectedYacht ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                    {errors.numberOfGuests && <p className="text-red-500 text-xs mt-1">{errors.numberOfGuests}</p>}
                  </div>
                </div>

                {/* Customer Information */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center"><FaUser className="mr-2 text-green-600"/>Customer Information *</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={`w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500`} />
                            {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                         </div>
                         <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={`w-full px-3 py-2 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500`} />
                             {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                         </div>
                         <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Email Address</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500`} />
                             {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                         </div>
                         <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Phone Number</label>
                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500`} />
                             {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                         </div>
                    </div>
                </div>

                {/* Partner Selection & Coupon */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center"><FaHandshake className="mr-2 text-purple-600"/>Professional Partner (Optional)</h4>
                        <select
                            name="professionalPartnerId"
                            value={formData.professionalPartnerId}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select Partner (if applicable)</option>
                            {allPartners.map(partner => (
                                <option key={partner._id} value={partner._id}>
                                    {partner.firstName} {partner.lastName} ({partner.companyName || 'No Company'})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center"><FaTag className="mr-2 text-orange-600"/>Coupon Code (Optional)</h4>
                        <input
                            type="text"
                            name="couponCode"
                            placeholder="Enter coupon code"
                            value={formData.couponCode}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 uppercase"
                        />
                        {/* Backend handles coupon validation, so no client-side status needed here */}
                    </div>
                </div>

                {/* Additional Notes */}
                 <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center"><FaStickyNote className="mr-2 text-yellow-600"/>Additional Notes</h4>
                    <textarea
                        name="additionalNotes"
                        rows="3"
                        placeholder="Any special requests or details..."
                        value={formData.additionalNotes}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                 </div>

                 {/* Submit Error */}
                 {errors.submit && (
                    <p className="text-red-600 text-sm text-center">{errors.submit}</p>
                 )}

              </form>
            )}
          </div>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-gray-100 px-6 py-4 border-t border-gray-200 flex justify-end gap-4 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit" // Connects to the form's onSubmit
              onClick={handleSubmit} // Also trigger submit logic on click
              disabled={loading || initialLoading}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 rounded-full shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" color="text-white" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FaShip />
                  <span>Create Booking</span>
                </>
              )}
            </button>
          </div>

        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateYachtBookingModal;
